import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  InputSignal,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, finalize, Observable, switchMap } from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ButtonStyleHelper,
  ComponentProvider,
  ConfigService,
  DEFAULT_CONFIG,
  encodeBase64Unicode,
  FormatCurrencyPipe,
  GsOptimizedImageComponent,
  GsResponsiveDirective,
  GsStructBorderRadius,
  ImageService,
  InformationService,
  ISearchBaseItem,
  ISides,
  ITypeaheadHeader,
  ITypeaheadRender,
  IUiButtonSettings,
  ResponsiveAttribute,
  StyleService,
  SystemService,
  TypeaheadItemType
} from 'libs/shared/src';
import { GsIconComponent } from 'libs/shared/src/components/gs-icon/gs-icon.component';
import { ModelType } from 'libs/shared/src/models/search/search-base-item';
import { SharedTeaserBadgeComponent } from '../shared/components/shared-teaser-badge/shared-teaser-badge.component';

/** Minimum number of characters required to trigger search */
const MIN_SEARCH_LENGTH = 3;

/** Types requested from the backend — only a fallback, configured types override this list server-side */
const DEFAULT_TYPEAHEAD_TYPES: ModelType[] = ['Article', 'Page', 'Report', 'Video', 'Category'];

/** How the items of a result section are rendered */
type TypeaheadSectionLayout = 'articles' | 'media' | 'links';

/** A result section of the typeahead */
export interface ITypeaheadSection {
  /** Key holding the items of this section inside `items.data` */
  key: string;
  /** Layout used to render the items */
  layout: TypeaheadSectionLayout;
  /** i18n key of the heading */
  titleKey?: string;
  /** i18n key of the "nothing found" text */
  emptyKey?: string;
  /** Item type for the teaser badge */
  badgeType?: ModelType;
}

/** Layout and translations per type, in display order */
const TYPEAHEAD_SECTIONS: Record<TypeaheadItemType, Omit<ITypeaheadSection, 'key'>> = {
  articles: {
    layout: 'articles',
    titleKey: 'header.typeahead.products',
    emptyKey: 'header.typeahead.noProducts',
    badgeType: 'Article'
  },
  reports: {
    layout: 'media',
    titleKey: 'header.typeahead.reports',
    emptyKey: 'header.typeahead.noReports',
    badgeType: 'Report'
  },
  pages: { layout: 'media', titleKey: 'header.typeahead.pages', emptyKey: 'header.typeahead.noPages', badgeType: 'Page' },
  videos: { layout: 'media', titleKey: 'header.typeahead.videos', emptyKey: 'header.typeahead.noVideos', badgeType: 'Video' },
  categories: { layout: 'links', titleKey: 'header.typeahead.categories', emptyKey: 'header.typeahead.noCategories' }
};

/**
 * Maps a backend type name to its group key in the response (mirrors `SortedDictionaryWithKeys`).
 * @param type Backend type name, e.g. `Article` or `Category`
 * @returns The response key, e.g. `articles` or `categories`
 */
function typeToTypeaheadKey(type: string): string {
  const plural = type === 'Category' ? 'Categories' : `${type}s`;
  return plural.charAt(0).toLowerCase() + plural.slice(1);
}

@Component({
  selector: 'typeahead',
  imports: [
    CommonModule,
    GsOptimizedImageComponent,
    GsIconComponent,
    FormatCurrencyPipe,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    SharedTeaserBadgeComponent,
    GsResponsiveDirective
  ],
  styleUrl: './typeahead.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './typeahead.component.html'
})
export class TypeaheadComponent {
  // Services
  protected readonly systemService: SystemService = inject(SystemService);
  protected readonly imageService: ImageService = inject(ImageService);
  protected readonly styleService: StyleService = inject(StyleService);
  protected readonly infoService: InformationService = inject(InformationService);
  private readonly buttonStyleHelper = inject(ButtonStyleHelper);
  private readonly componentService: ComponentProvider = inject(ComponentProvider);
  private readonly configService = inject(ConfigService);
  private readonly router: Router = inject(Router);

  // Inputs & Models
  /** Should the typeahead be displayed in full-size? */
  showFullSize: InputSignal<boolean> = input(false);
  /** Border radius for the result images */
  borderRadius: InputSignal<GsStructBorderRadius | undefined> = input<GsStructBorderRadius | undefined>(undefined);

  // Properties & Signals
  /** Is the searchInput currently shown? */
  showSearchInput: WritableSignal<boolean> = signal(false);
  /** The form of the search-input */
  searchForm: FormControl<string | null> = new FormControl<string | null>({ value: '', disabled: false }, [
    Validators.required,
    Validators.minLength(MIN_SEARCH_LENGTH)
  ]);
  /** The adata loaded from the backend after valid search */
  typeaheadData: WritableSignal<ITypeaheadRender | undefined> = signal(undefined);
  /** Is currently something in the search loading? */
  isLoading: WritableSignal<boolean> = signal(false);
  /** Sections to render, in display order */
  sections: Signal<ITypeaheadSection[]> = computed(() => {
    const active = new Set(this.activeTypeKeys());
    const known = Object.keys(TYPEAHEAD_SECTIONS).filter((key) => active.has(key));
    const unknown = [...active].filter((key) => !(key in TYPEAHEAD_SECTIONS));
    // Types without a dedicated layout still get a link list, so no configured type stays invisible
    return [...known, ...unknown].map((key) => ({ key, ...(TYPEAHEAD_SECTIONS[key as TypeaheadItemType] ?? { layout: 'links' }) }));
  });
  /** Sections shown in the wide column (items with an image) */
  mainSections: Signal<ITypeaheadSection[]> = computed(() => this.sections().filter((section) => section.layout !== 'links'));
  /** Sections shown in the narrow column (plain link lists) */
  asideSections: Signal<ITypeaheadSection[]> = computed(() => this.sections().filter((section) => section.layout === 'links'));
  /**
   * Horizontal padding of the header, resolved per breakpoint in CSS via `[gsResponsive]`.
   * The full-size typeahead adopts it so its content lines up with the header of the same project.
   */
  headerPadding: Signal<ResponsiveAttribute<ISides> | undefined> = computed(
    () => this.configService.config()?.header?.padding ?? DEFAULT_CONFIG.header?.padding
  );
  /** Path of the search page with the current term, undefined when no search page is configured */
  searchPath: Signal<string | undefined> = computed(() => {
    const searchUrl = this.infoService.init()?.linkTargets?.search;
    if (!searchUrl) return undefined;
    const searchTerm = this.searchTerm();
    return this.router.createUrlTree([searchUrl], searchTerm ? { queryParams: { q: searchTerm } } : {}).toString();
  });
  /** Current value of the search input */
  private readonly searchTerm: Signal<string | null> = toSignal(this.searchForm.valueChanges, {
    initialValue: this.searchForm.value
  });
  /** Response keys of the configured types — from the response once it arrived, from the settings before */
  private readonly activeTypeKeys: Signal<string[]> = computed(() => {
    const data = this.typeaheadData()?.items?.data;
    if (data) return Object.keys(data);
    // Empty counts as "not configured" — an init payload cached before the backend guard can hold []
    const configured = this.infoService.init()?.typeaheadTypes;
    return (configured?.length ? configured : DEFAULT_TYPEAHEAD_TYPES).map(typeToTypeaheadKey);
  });

  // Getters & Setters
  /** @returns Merged button settings from defaults and global config */
  get buttonSettings(): Partial<IUiButtonSettings> {
    return {
      ...DEFAULT_CONFIG.uiElements?.buttons,
      ...this.configService.config()?.uiElements?.buttons
    };
  }
  /** @returns Primary button CSS classes */
  get actionButtonClasses(): string {
    return this.buttonStyleHelper.getPrimaryButtonClasses(this.buttonSettings);
  }
  /** @returns Inline styles for button padding and font */
  get actionButtonStyles(): Record<string, string> {
    return this.buttonStyleHelper.getButtonStyles(this.buttonSettings);
  }

  // Host-Listeners
  /** Press ESC to reset the search */
  @HostListener('document:keydown.escape')
  handleEscape() {
    this.resetSearch();
  }

  // ViewChilds
  /** The input of the search-text value (for focus) */
  searchInputElement = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  constructor() {
    // Loads the suggestions for the debounced search term. switchMap cancels the request of an outdated
    // term, so a late response can't overwrite the results of a newer one; an invalid term only cancels.
    this.searchForm.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((term) => (this.searchForm.valid ? this.loadTypeaheadData(term) : EMPTY)),
        takeUntilDestroyed()
      )
      .subscribe((data) => {
        // A response that lands after resetSearch() must not refill the closed box
        if (this.searchForm.valid) this.typeaheadData.set(data);
      });

    // Autofocus the input when it is shown
    toObservable(this.showSearchInput)
      .pipe(debounceTime(10), takeUntilDestroyed())
      .subscribe((show) => {
        if (show && this.searchInputElement()?.nativeElement) this.searchInputElement()?.nativeElement.focus();
      });
  }

  // Methods
  /**
   * Resets the search input and hides the typeahead box
   */
  resetSearch() {
    this.searchForm.reset();
    this.typeaheadData.set(undefined);
    this.showSearchInput.set(false);
  }

  /**
   * Items of a result section
   * @param key The response key of the section
   * @returns The items, or an empty array when the section has no results
   */
  itemsForSection(key: string): ISearchBaseItem[] {
    return this.typeaheadData()?.items?.data?.[key] ?? [];
  }

  /**
   * Is true when search has results for given type
   * @param key The response key to check for
   * @returns True if there are results for the given type
   */
  hasResultsForType(key: string): boolean {
    return this.itemsForSection(key).length > 0;
  }

  /**
   * Is true when any rendered section has results
   * @returns True if at least one section has results
   */
  hasAnyResults(): boolean {
    return this.sections().some((section) => this.hasResultsForType(section.key));
  }

  /**
   * Navigates to the search page with the current search term as query parameter
   */
  navigateToSearch(): void {
    const searchPath = this.searchPath();
    if (!searchPath) {
      console.warn('Search URL is not defined in linkTargets');
      return;
    }
    this.systemService.openLink(searchPath);
    this.resetSearch();
  }

  /**
   * Handles form submission (triggered by Enter key or clicking the search button)
   * @param event The form submit event
   */
  onSearchSubmit(event: Event): void {
    event.preventDefault();
    this.navigateToSearch();
  }

  /**
   * Loads data for the typeahead component to display search suggestions.
   * The search input is encoded in base64 before sending the request.
   * @param term The search term
   * @returns The suggestions; completes without a value when the request fails
   */
  private loadTypeaheadData(term: string | null): Observable<ITypeaheadRender> {
    this.isLoading.set(true);
    const searchValue = term ? encodeBase64Unicode(term) : '';

    return this.componentService
      .get<ITypeaheadRender, ITypeaheadHeader>('Typeahead', {
        code: {
          Types: DEFAULT_TYPEAHEAD_TYPES.join(', '),
          Search: searchValue,
          PageSize: 5
        }
      })
      .pipe(
        catchError((err) => {
          console.error('Error loading typeahead data:', err);
          return EMPTY;
        }),
        // Also runs when switchMap cancels the request, so the spinner never sticks
        finalize(() => this.isLoading.set(false))
      );
  }
}
